import { EyeOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import moment from 'moment';
import { always, assocPath, compose, cond, defaultTo, dissocPath, equals, filter, find, identity, is, isEmpty, map, path, pathOr, pick, prop, propEq, propOr, T, uniqBy, isNotNil, isNotEmpty, any } from 'ramda';
import React, { useEffect, useState } from 'react';
import { correspondenceVariableLabel } from '../../helpers/flexigets';
import { useDocumentContext } from '../../../../../context/Document';
import ModalAddMetadata from '../ModalAddMetadata';
import withUser from '../../../../../withUser';
import Switch from '../../../../../components/Switch';
import { traverseAndCheckRules } from '../../helpers/pages';
import TextVariable from './variablesTab/TextVariable';
import DateVariable from './variablesTab/DateVariable';
import SelectVariable from './variablesTab/SelectVariable';
import ListVariables from './variablesTab/ListVariables';
import JSONVariable from './variablesTab/JSONVariable';

const VariablesTab = ({ userIsAdmin }) => {
  const {
    isEdit,
    previewDocumentHtml,
    widgetsAreDefined,
    previewHtmlLoading,
    handleSaveBtn,
    nameIsDefined,
    loading,
    setState,
    values,
    globalVariables,
    updateIsVariablesHighlighted,
    variables,
    widgetsOnDocument,
    userIsOfferEditor
  } = useDocumentContext();
  const userCanEdit = userIsAdmin || userIsOfferEditor;
  const [isComponentMounted, setIsComponentMounted] = useState(false);
  const [currentIsVariablesHighlighted, setCurrentIsVariablesHighlighted] = useState(prop('isVariablesHighlighted', values));
  const [previewHighlightedVariables, setPreviewHighlightedVariables] = useState(false);

  const usedGlobalVariablesInDocument = filter(globalVariable => {
    const variableName = prop('name', globalVariable);
    const pages = propOr([], 'pages', values);
    const matchedRules = traverseAndCheckRules(pages, rule => {
      const conditions = propOr([], 'conditions', rule);
      return any(condition => propEq(variableName, 'variable', condition), conditions);
    });

    return !isEmpty(matchedRules);
  }, globalVariables);

  const globalVariablesUsedOnWidgets = filter(globalVariable => {
    const regex = new RegExp(`"variable"\\s*:\\s*"${prop('name', globalVariable)}"`);
    return any(widget => regex.test(prop('contents', widget) || ''), widgetsOnDocument);
  }, globalVariables);

  const globalVariablesUsed = uniqBy(prop('name'), [...usedGlobalVariablesInDocument, ...globalVariablesUsedOnWidgets]);

  useEffect(() => {
    setIsComponentMounted(true);
  }, []);

  useEffect(() => {
    if (isComponentMounted) {
      previewDocumentHtml();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewHighlightedVariables]);

  if (isEmpty(globalVariablesUsed) && (isEmpty(variables) || !widgetsAreDefined)) {
    return null;
  }

  const onRemoveVariable = (name) => setState(dissocPath(['values', 'variables', name]));

  const onChangeVariable = (key, type) => (value) => {
    if (equals('', value)) {
      onRemoveVariable(key);
    } else {
      const parsedValue = equals('JSON', type) && is(String, value) ? JSON.parse(value) : value;
      setState(compose(
        assocPath(['values', 'variables', key, 'value'], parsedValue),
        assocPath(['values', 'variables', key, 'type'], type)
      ));
    }
  };

  const onChangeMultipleVariables = (metadata) => map(({ name, type, value }) => onChangeVariable(name, type)(value), metadata);

  const onChangeIsDefaultValue = (key) => (bool) => setState(assocPath(['values', 'variables', key, 'isDefaultValue'], bool));

  const onChangeIsVariablesHighlighted = () => {
    const newValue = !currentIsVariablesHighlighted;
    updateIsVariablesHighlighted(newValue);
    setCurrentIsVariablesHighlighted(newValue);
    setPreviewHighlightedVariables(!previewHighlightedVariables);
  };

  return (
    <div className="mt-4">
      <span className="text-xl uppercase text-flexi-text font-bold">Saisir des variables</span>
      <p className="italic text-xs">Les valeurs sont utilisées dans la génération seulement si elles sont considérées comme valeurs par défaut.</p>
      <div className="scrollbar overflow-y-scroll h-582px pt-3">
        <div className="w-11/12">
          <div className="flex items-center space-x-5">
            <ModalAddMetadata
              variablesValues={prop('variables', values)} globalVariables={globalVariablesUsed} variables={variables}
              onChangeMultipleVariables={onChangeMultipleVariables}
            />
            <Switch
              className="flex items-center"
              checked={currentIsVariablesHighlighted}
              onChange={onChangeIsVariablesHighlighted}
              loading={previewHtmlLoading}
              label="Surligner les variables dans la prévisualisation"
              onClick={onChangeIsVariablesHighlighted}
            />
          </div>
          {isNotEmpty(globalVariablesUsed) && (
            <>
              <hr className="my-8"/>
              <span className="text-lg font-medium">{'Variables globales de l\'offre'}</span>
              {map(({ name, type }) => {
                const isDefaultValue = pathOr(false, ['variables', name, 'isDefaultValue'])(values);
                const value = pathOr('', ['variables', name, 'value'])(values);

                if (equals('boolean', type)) {
                  return (
                    <SelectVariable
                      key={name}
                      label={name}
                      value={value}
                      options={[{ value: true, title: 'Oui' }, { value: false, title: 'Non' }]}
                      onChange={onChangeVariable(name, type)}
                      onChangeIsDefaultValue={onChangeIsDefaultValue(name)}
                      isDefaultValue={isDefaultValue}
                      userCanEdit={userCanEdit}
                    />
                  );
                }

                return (
                  <TextVariable
                    key={name}
                    label={name}
                    value={value}
                    onBlur={onChangeVariable(name, type)}
                    type={type}
                    onChangeIsDefaultValue={onChangeIsDefaultValue(name)}
                    isDefaultValue={isDefaultValue}
                    userCanEdit={userCanEdit}
                  />
                );
              })(globalVariablesUsed)}
            </>
          )}
          {isNotEmpty(globalVariablesUsed) && isNotEmpty(variables) && <hr className="my-8"/>}
          {isNotEmpty(variables) && (
            <>
              <span className="text-lg font-medium">Variables du document</span>
              {map(({ type, key, fields, options }) => {
                const getStringInHtmlTag = (key) => {
                  const regex = /<[^>]*>/g;
                  return key.replace(regex, '');
                };
                const labelWithoutHTML = key && getStringInHtmlTag(key);
                const label = propOr(labelWithoutHTML, key, correspondenceVariableLabel);

                const defaultValue = compose(
                  cond([
                    [equals('false'), always('Non')],
                    [equals('true'), always('Oui')],
                    [T, identity]
                  ]),
                  (variable) => {
                    const type = prop('type', variable);
                    const valueByDefault = equals('checkbox', type) ? 'false' : '';
                    return propOr(valueByDefault, 'default', variable);
                  },
                  find(v => v.key === key)
                )(variables);

                const isDefaultValue = pathOr(false, ['variables', key, 'isDefaultValue'])(values);

                if (equals('list', type)) {
                  return (
                    <ListVariables
                      key={key}
                      label={label}
                      values={pathOr([], ['variables', key])(values)}
                      fields={defaultTo([], fields)}
                      setState={setState}
                      onChangeIsDefaultValue={onChangeIsDefaultValue(key)}
                      isDefaultValue={isDefaultValue}
                      userCanEdit={userCanEdit}
                    />
                  );
                }

                if (equals('checkbox', type)) {
                  return (
                    <SelectVariable
                      key={key}
                      label={label}
                      value={pathOr(defaultValue, ['variables', key, 'value'])(values)}
                      options={options}
                      onChange={onChangeVariable(key, type)}
                      onChangeIsDefaultValue={onChangeIsDefaultValue(key)}
                      isDefaultValue={isDefaultValue}
                      userCanEdit={userCanEdit}
                    />
                  );
                }

                if (equals('date', type)) {
                  return (
                    <DateVariable
                      key={key}
                      label={label}
                      format="DD/MM/YYYY"
                      value={isNotNil(path(['variables', key, 'value'])(values)) ? moment(path(['variables', key, 'value'])(values), 'DD/MM/YYYY') : ''}
                      onChange={onChangeVariable(key, type)}
                      onChangeIsDefaultValue={onChangeIsDefaultValue(key)}
                      isDefaultValue={isDefaultValue}
                      userCanEdit={userCanEdit}
                    />
                  );
                }

                if (equals('multi_select', type)) {
                  return (
                    <SelectVariable
                      key={key}
                      label={label}
                      value={path(['variables', key, 'value'])(values)}
                      options={options}
                      onChange={onChangeVariable(key, type)}
                      multiple
                      onChangeIsDefaultValue={onChangeIsDefaultValue(key)}
                      isDefaultValue={isDefaultValue}
                      userCanEdit={userCanEdit}
                    />
                  );
                }

                if (equals('JSON', type)) {
                  const JSONValue = compose(
                    pick(['rows']),
                    pathOr({}, ['variables', key, 'value'])
                  )(values);

                  return (
                    <JSONVariable
                      key={key}
                      label={label}
                      value={JSON.stringify(JSONValue, null, 2)}
                      isDefaultValue={isDefaultValue}
                      onChangeIsDefaultValue={onChangeIsDefaultValue(key)}
                      onChange={onChangeVariable(key, type)}
                      userCanEdit={userCanEdit}
                    />
                  );
                }

                return (
                  <TextVariable
                    key={key}
                    label={label}
                    value={pathOr(defaultValue, ['variables', key, 'value'])(values)}
                    isDefaultValue={isDefaultValue}
                    onBlur={onChangeVariable(key, type)}
                    onChangeIsDefaultValue={onChangeIsDefaultValue(key)}
                    userCanEdit={userCanEdit}
                  />
                );
              })(variables)}
            </>
          )}
        </div>
      </div>
      <div className="flex mt-10">
        <Button
          className="text-sm w-full"
          onClick={previewDocumentHtml}
          loading={previewHtmlLoading}
          icon={<EyeOutlined />}
        >
          Prévisualiser
        </Button>

        {userCanEdit && (
          <Button
            type="primary"
            className="text-sm w-full ml-2"
            onClick={() => handleSaveBtn(false)}
            disabled={!nameIsDefined}
            loading={loading}
            icon={<ThunderboltOutlined />}
          >
            <span className="font-semibold">{isEdit ? 'Sauvegarder le document' : 'Créer le document'}</span>
          </Button>
        )}
      </div>
    </div>
  );
};

export default withUser(VariablesTab);
